<template>
    <nav v-if="allocation.hasOrderServices" class="order-service-list">
        <VButton
            class="order-service-btn rounded"
            v-for="service in allocation.orderServices"
            :key="service.id"
            :class="{
                primary: isActiveOrderService(service),
                outlined: !isActiveOrderService(service),
                secondary: !isActiveOrderService(service),
            }"
            @click="setAsActiveOrderService(service)"
        >
            {{ translateByLocationLanguage(service.useCase?.name) }}
        </VButton>
    </nav>
    <div v-else class="no-order-service-msg">
        {{ trans_vue.offerSection.noOrderServiceConfigured }}
    </div>
</template>

<script setup lang="ts">
    import { Popup } from "@/components/dialog/Popup";
    import type { IService } from "@/data/model/Service";
    import { useCartStore } from "@/data/stores/cart";
    import { useOfferStore } from "@/data/stores/offer";
    import { useTableAllocationStore } from "@/data/stores/tableAllocation";
    import { trans } from "@/operator/localization/Trans";
    import { trans_vue } from "@/operator/localization/TransVue";
    import { translate, translateByLocationLanguage } from "@/shared/localization/language";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";

    import VButton from "@/components/elements/VButton.vue";

    const allocation = useTableAllocationStore();
    const offer = useOfferStore();
    const cart = useCartStore();

    function isActiveOrderService(service: IService) {
        return offer.orderService?.id === service.id;
    }

    async function setAsActiveOrderService(service: IService) {
        try {
            const hasCartItemsForOtherOrderService = cart.hasItems && cart.orderService?.id != service.id;
            if (hasCartItemsForOtherOrderService) {
                const warningMessage = trans_vue.cart.haveItemsInCart.replace(
                    "{0}",
                    `<b>${translate(cart.orderService?.useCase?.name)}</b>`
                );
                Popup.show({
                    uniqueId: "items-in-cart",
                    message: warningMessage,
                    confirmButton: translations.general.continue,
                    cancelButton: translations.general.cancel,
                    highlightConfirm: false,
                    highlightCancel: false,
                    confirmHandler: async () => {
                        await cart.deleteCartsFromLocation();
                        await offer.setOrderService(service);
                    },
                });
            } else {
                await offer.setOrderService(service);
            }
        } catch (ex) {
            logError(ex);
            showError(trans_vue.orderAndPayView.failedLoadingOffer);
        }
    }

    function showError(message: string) {
        Popup.show({
            title: trans.general.error,
            message: message,
        });
    }
</script>

<style scoped lang="scss">
    .order-service-list {
        white-space: nowrap;
        display: flex;
        align-items: center;
        padding: 0 1em;

        .order-service-btn {
            margin: 0 2px;
            font-size: 16px;
            font-weight: 500;
        }
    }

    .no-order-service-msg {
        display: flex;
        align-items: center;
        padding: 0 1em;
    }
</style>
